// Rich menu definitions.
// Customer menu, "branded" (2500×1336, the default): shop name bar, banner picture, then 4 buttons in one row.
// Customer menu, "six" (2500×1686): the earlier 6-button version, still accepted for pictures saved before.
// Customer menu, "compact" (2500×843): 4 equal buttons, no name bar or banner (owners' own simple pictures).
// Staff menu: 2500×843, 4 equal buttons.
import { liffUrl } from './messages.ts';

export const MENU_WIDTH = 2500;
export const COMPACT_HEIGHT = 843;
export const BRANDED_HEIGHT = 1336;
export const SIX_HEIGHT = 1686;

/** Default customer menu layout, shared by the tap areas below and the picture designer in the web app. */
export const BRANDED_LAYOUT = {
  header: { y: 0, height: 220 },
  banner: { y: 220, height: 735 },
  buttons: { y: 955, height: BRANDED_HEIGHT - 955, cols: 4 },
} as const;

export type CustomerMenuLayout = 'branded' | 'six' | 'compact';

/** Which layout a customer menu picture of this size uses, or null when the size is not allowed. */
export function layoutForSize(width: number, height: number): CustomerMenuLayout | null {
  if (width !== MENU_WIDTH) return null;
  if (height === BRANDED_HEIGHT) return 'branded';
  if (height === SIX_HEIGHT) return 'six';
  if (height === COMPACT_HEIGHT) return 'compact';
  return null;
}

interface Area { x: number; y: number; width: number; height: number; label: string; uri: string }

function menu(name: string, height: number, areas: Area[]): Record<string, unknown> {
  return {
    size: { width: MENU_WIDTH, height },
    selected: true,
    name,
    chatBarText: 'เมนู',
    areas: areas.map((a) => ({
      bounds: { x: a.x, y: a.y, width: a.width, height: a.height },
      action: { type: 'uri', label: a.label, uri: a.uri },
    })),
  };
}

/** Splits `total` into `n` whole-pixel parts that add up exactly (e.g. 2500 / 3 → 833, 833, 834). */
function split(total: number, n: number): { start: number; size: number }[] {
  return Array.from({ length: n }, (_, i) => {
    const start = Math.round((total * i) / n);
    return { start, size: Math.round((total * (i + 1)) / n) - start };
  });
}

function row(items: { label: string; uri: string }[], y: number, height: number): Area[] {
  return split(MENU_WIDTH, items.length).map((c, i) => ({ x: c.start, y, width: c.size, height, ...items[i] }));
}

export interface CustomerMenuShop { phone: string | null; mapUrl?: string | null }

/** The 4 customer buttons of the default menu, left to right. */
export function customerButtons(liffId: string, shop: CustomerMenuShop): { label: string; uri: string }[] {
  const u = (p: string) => liffUrl(liffId, p) as string;
  return [
    { label: 'จองคิว', uri: u('/book') },
    { label: 'การจองของฉัน', uri: u('/my') },
    { label: 'โทรหาร้าน', uri: shop.phone ? `tel:${shop.phone}` : u('/info') },
    { label: 'บริการและราคา', uri: u('/info') },
  ];
}

/** Brand bar → shop info, banner → book (both layouts with a banner). */
function topAreas(u: (p: string) => string): Area[] {
  const { header, banner } = BRANDED_LAYOUT;
  return [
    { x: 0, y: header.y, width: MENU_WIDTH, height: header.height, label: 'ข้อมูลร้าน', uri: u('/info') },
    { x: 0, y: banner.y, width: MENU_WIDTH, height: banner.height, label: 'จองคิว', uri: u('/book') },
  ];
}

export function customerRichMenu(liffId: string, shop: CustomerMenuShop, layout: CustomerMenuLayout = 'branded'): Record<string, unknown> {
  const u = (p: string) => liffUrl(liffId, p) as string;
  const call = shop.phone ? `tel:${shop.phone}` : u('/info');
  if (layout === 'compact') {
    return menu('salonflow-customer', COMPACT_HEIGHT, row([
      { label: 'จองคิว', uri: u('/book') },
      { label: 'การจองของฉัน', uri: u('/my') },
      { label: 'ข้อมูลร้าน', uri: u('/info') },
      { label: 'โทรหาร้าน', uri: call },
    ], 0, COMPACT_HEIGHT));
  }
  if (layout === 'six') {
    const y = BRANDED_LAYOUT.buttons.y;
    const rows = split(SIX_HEIGHT - y, 2);
    const six = [
      { label: 'จองคิว', uri: u('/book') },
      { label: 'การจองของฉัน', uri: u('/my') },
      { label: 'บริการและราคา', uri: u('/info') },
      { label: 'แผนที่ร้าน', uri: shop.mapUrl && /^https:\/\/\S+$/.test(shop.mapUrl) ? shop.mapUrl : u('/info') },
      { label: 'โทรหาร้าน', uri: call },
      { label: 'บัญชีของฉัน', uri: u('/profile') },
    ];
    return menu('salonflow-customer', SIX_HEIGHT, [
      ...topAreas(u),
      ...rows.flatMap((r, i) => row(six.slice(i * 3, (i + 1) * 3), y + r.start, r.size)),
    ]);
  }
  const { buttons } = BRANDED_LAYOUT;
  return menu('salonflow-customer', BRANDED_HEIGHT, [
    ...topAreas(u),
    ...row(customerButtons(liffId, shop), buttons.y, buttons.height),
  ]);
}

export function staffRichMenu(liffId: string): Record<string, unknown> {
  const u = (p: string) => liffUrl(liffId, p) as string;
  return menu('salonflow-staff', COMPACT_HEIGHT, row([
    { label: 'คิววันนี้', uri: u('/staff/today') },
    { label: 'ปฏิทิน', uri: u('/staff/calendar') },
    { label: 'จองให้ลูกค้า', uri: u('/staff/book') },
    { label: 'จัดการร้าน', uri: u('/staff/manage') },
  ], 0, COMPACT_HEIGHT));
}
